import React, { useEffect, useState, useCallback, createRef } from "react";
import { useParams, useNavigate } from "react-router";
import { Formik } from "formik";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import { ASSET_REPLAY_LOGS_QUERY } from "../../../constants/graphql/queries";
import {
  ADD_SHOPPING_INVENTORY_ITEM_MUTATION,
  CREATE_INVENTORY_TRANSACTION_MUTATION,
} from "../../../constants/graphql";
import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import { formRef } from "../../../views/capture/Form";
import useQueryParams from "../../../hooks/useQueryParams";
import Fields, { getRestockTotal } from "./Fields";
import PalletButton from "./pallet/PalletButton";
import useInitialValues from "./hooks/useInitialValues";
import { getFormattedLocations, getOldRackLocations, getRackLocationsChanges } from "./utils";
import InventoryTransactionActions from "./InventoryTransactionActions";
import InventoryMoveAssistant from "./moveAssistant/InventoryMoveAssistant";
import InventoryMoveByItemIdAssistant from "./moveByItemIdAssistant/InventoryMoveByItemIdAssistant";
import QuantityCalculation from "./quantityCalculation/QuantityCalculation";
import { fullWidthDialog } from "src/constants/dialog";
import useAuth from "../../../hooks/useAuth";

const schema = Yup.object().shape({
  locations: Yup.array().of(
    Yup.object().shape({
      warehouse: Yup.object()
        .shape({
          id: Yup.number().required("Warehouse ID is required"),
        })
        .required("Warehouse is required"),
      warehouse_locations: Yup.array()
        .of(
          Yup.object().shape({
            id: Yup.number().required("Location ID is required"),
          })
        )
        .min(1, "At least one storage location is required")
        .required("Storage locations are required"),
    })
  ),
});

export const inventoryTransactionFormRef = createRef();

export default function InventoryTransactionMultipleModal({
  open,
  onClose,
  onSuccess,
  title,
  asset,
  displayPalletBreakdown = false,
  displayQuantityCalculation = true,
  displayNewAvailableQuantity = true,
  displayTotalQuantityChange = true,
  displayShipmentId = true,
  displayBulkActions = true,
  displayAddAnotherLocation = true,
  displayDeleteLocation = true,
  displayDuplicate = true,
  openMoveAssistantModal = false,
  openMoveItemAssistantModal = false,
  hideLocation,
  submitLabel = "Submit",
  shipmentItem,
  shipment,
  sameLocation,
  shipmentItemQuantity,
  onShipmentItemQuantityUpdate,
  actionSource = "transaction_modal",
  disableQuantity = false,
  secondaryAction,
}) {
  const { user } = useAuth();
  const { settings } = user.company;
  const [openMoveAssistant, setOpenMoveAssistant] = useState(openMoveAssistantModal);
  const [openMoveItemAssistant, setOpenMoveItemAssistant] = useState(openMoveItemAssistantModal);
  const navigate = useNavigate();
  const { projectId: routeProjectId } = useParams();
  const params = useQueryParams();
  const shipmentItemId = params.get("shipmentItemId");
  const [loading, setLoading] = useState(false);
  const projectId = asset?.project?.id || asset?.projectId || routeProjectId;

  const [multiSelect, setMultiSelect] = useState(false);
  const [createTransactionMutation] = useMutation(CREATE_INVENTORY_TRANSACTION_MUTATION);
  const [addInventoryItem] = useMutation(ADD_SHOPPING_INVENTORY_ITEM_MUTATION);
  const { notifySuccess, notifyError } = useNotification();
  const initialStep = settings.pallet_breakdown_wizard && shipmentItemId ? 0 : -1;
  const [step, setStep] = useState(initialStep);

  const initialValues = useInitialValues(asset, sameLocation);

  const onSubmit = values => {
    let locations = getFormattedLocations(values, true);

    if (shipment) {
      const existingNonShipmentLocations = asset.rackLocations.filter(
        loc => loc.shipment_id !== shipment.shipmentId
      );
      locations = [...existingNonShipmentLocations, ...locations];
    }

    const oldRackLocations = getOldRackLocations(initialValues.locations);
    const rackLocationsChanges = getRackLocationsChanges(values.locations, initialValues.locations);
    const restockTotal = getRestockTotal(values.locations) - values.deletedQuantity;

    setLoading(true);

    const variables = {
      code: values.code,
      notes: values.notes || "",
      senderName: values.senderName || "",
      carrierName: values.carrierName || "",
      trackingNumber: values.trackingNumber || "",
      orderNumber: values.orderNumber || "",
      poNumber: values.poNumber || "",
      quantity: restockTotal,
      rackLocations: locations,
      rackLocationsChanges,
      oldRackLocations,
      id: values.id,
      actionSource,
    };

    if (shipmentItemId) {
      variables.shipmentItemId = shipmentItemId;
    }

    const refetchQueries = [{ query: ASSET_REPLAY_LOGS_QUERY, variables: { assetId: values.id } }];

    createTransactionMutation({
      variables,
      refetchQueries,
      onSuccess: data => {
        setLoading(false);
        notifySuccess("Asset successfully updated!");
        onSuccess(data, values);
        if (shipmentItemId) {
          const params = new URLSearchParams(window.location.search);
          params.set("rackLocationsChanges", JSON.stringify(rackLocationsChanges));
          navigate({ search: params.toString() });
        }
      },
      onFailure: errors => {
        notifyError(errors.submit);
        setLoading(false);
      },
    });
  };

  const restockAddToCart = values => {
    const restockTotal = getRestockTotal(values.locations);
    onSubmit(values).then(() => {
      if (restockTotal > 0) {
        addInventoryItem({
          variables: {
            assetId: values.id,
            quantity: restockTotal,
            projectId,
          },
        });
      }
    });
  };

  const handleToggleMultiSelect = () => {
    setMultiSelect(v => !v);
  };

  const handleHideLocation = useCallback(params => hideLocation({ initialValues, ...params }), [
    hideLocation,
    initialValues,
  ]);

  useEffect(() => {
    if (formRef?.current?.dirty) {
      const saveButton = document.querySelector("#saveAsset");
      if (saveButton) {
        saveButton.click();
      }
    }
  }, []);

  const openPalletBreakdown = useCallback(() => {
    setStep(0);
  }, [setStep]);

  return (
    <Dialog open={open} fullWidth maxWidth="md" sx={fullWidthDialog}>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={schema}
          enableReinitialize
          innerRef={inventoryTransactionFormRef}
        >
          <Box minHeight={340} display="flex" flexDirection="column" justifyContent="space-between">
            <Box display="flex" justifyContent="center" alignItems="center" p={1} mb={3}>
              <Typography
                component="span"
                color="text.primary"
                fontSize={{ xs: 21, sm: 24 }}
                fontWeight={500}
                align="center"
              >
                {title}
              </Typography>
              {displayPalletBreakdown && <PalletButton step={step} setStep={setStep} />}
              {displayQuantityCalculation && <QuantityCalculation asset={asset} />}
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ position: "absolute", right: 16, top: 16, zIndex: 10 }}
            >
              <CloseIcon />
            </IconButton>
            <Grid container spacing={1}>
              {asset && (
                <Fields
                  multiSelect={multiSelect}
                  asset={asset}
                  openPalletBreakdown={openPalletBreakdown}
                  displayNewAvailableQuantity={displayNewAvailableQuantity}
                  displayTotalQuantityChange={displayTotalQuantityChange}
                  displayShipmentId={displayShipmentId}
                  displayAddAnotherLocation={displayAddAnotherLocation}
                  displayDeleteLocation={displayDeleteLocation}
                  displayDuplicate={displayDuplicate}
                  hideLocation={hideLocation ? handleHideLocation : null}
                  projectId={projectId}
                  disableQuantity={disableQuantity}
                  shipmentItemQuantity={shipmentItemQuantity}
                  onShipmentItemQuantityUpdate={onShipmentItemQuantityUpdate}
                />
              )}
            </Grid>
            <Box pt={5}>
              <InventoryTransactionActions
                loading={loading}
                initialValues={initialValues}
                submitLabel={submitLabel}
                displayBulkActions={displayBulkActions}
                restockAddToCart={restockAddToCart}
                onToggleMultiSelect={handleToggleMultiSelect}
                secondaryAction={secondaryAction}
              />
            </Box>
            {openMoveAssistant && projectId && (
              <InventoryMoveAssistant
                projectId={projectId}
                step={step}
                onClose={() => setOpenMoveAssistant(false)}
                shipmentItem={shipmentItem}
                shipment={shipment}
                asset={asset}
              />
            )}
            {openMoveItemAssistant && projectId && (
              <InventoryMoveByItemIdAssistant
                projectId={projectId}
                onClose={() => setOpenMoveItemAssistant(false)}
              />
            )}
          </Box>
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
